import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Eye, EyeOff, ArrowRight, ArrowLeft, TrendingUp, AlertTriangle, DollarSign, Shield, Mail, CheckCircle,
  LayoutDashboard, Sparkles, BarChart3, Users, Cloud, PiggyBank, Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { MFAVerification } from '../components/MFAComponents';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, user, loading: authLoading } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Initial tab from path — /signup, /register, /sign-up route variants land on the Register tab
  const _initialTab = /^\/(signup|register|sign-up|join)/i.test(location.pathname) ? 'register' : 'login';
  const [activeTab, setActiveTab] = useState(_initialTab);
  
  // OTP State (replaces CAPTCHA)
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpEmailHint, setOtpEmailHint] = useState('');
  const [otpMethods, setOtpMethods] = useState(['email']);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    role: 'engineering' 
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Handle SSO redirect — if IdP sent us back with ?sso_token or ?sso_error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    const ssoError = params.get('sso_error');
    if (ssoToken) {
      localStorage.setItem('token', ssoToken);
      toast.success('Welcome! Signed in via SSO.');
      window.history.replaceState({}, '', '/login');
      window.location.href = '/dashboard';
    } else if (ssoError) {
      const errDesc = params.get('sso_error_description') || '';
      toast.error(`SSO sign-in failed: ${ssoError}${errDesc ? ` — ${errDesc}` : ''}`);
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // Splash screen timer
  // Splash screen auto-disappears after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setSplashFading(true);
      setTimeout(() => setShowSplash(false), 500);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });
      const data = response.data;
      
      if (data.otp_required) {
        // OTP verification needed
        setOtpRequired(true);
        setOtpToken(data.otp_token);
        setOtpEmailHint(data.email_hint || '');
        setOtpMethods(data.mfa_methods || ['email']);
        toast.success('Verification code sent to your email');
        setLoading(false);
        return;
      }
      
      // Direct login (shouldn't happen with new flow, but handle anyway)
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        toast.success('Welcome back!');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login/verify-otp`, {
        code: otpCode,
        method: 'email'
      }, {
        headers: { 'X-OTP-Token': otpToken }
      });
      
      const data = response.data;
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        toast.success('Welcome back!');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setOtpCode('');
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await axios.post(`${API_URL}/auth/mfa/send-email-otp`, {}, {
        headers: { 'X-MFA-Token': otpToken }
      });
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error('Failed to resend code. Please try logging in again.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Pull a referral code if the visitor came in through /r/<code>.
      // Cookie set by ReferralLanding takes precedence; localStorage is the
      // belt-and-suspenders fallback for browsers that block cross-page cookies.
      const cookieRef = document.cookie.split('; ').find((c) => c.startsWith('cartie_ref='));
      const refCode = cookieRef
        ? decodeURIComponent(cookieRef.split('=')[1])
        : (localStorage.getItem('cartie_ref') || null);
      await register(
        registerData.email, registerData.password, registerData.name,
        registerData.role, refCode,
      );
      toast.success('Account created successfully!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotEmail });
      setResetSent(true);
    } catch (error) {
      setResetSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setResetSent(false);
    setForgotEmail('');
  };

  const features = [
    { icon: TrendingUp, title: 'Cost Forecasting', desc: 'AI-powered predictions' },
    { icon: AlertTriangle, title: 'Anomaly Detection', desc: 'Real-time alerts' },
    { icon: DollarSign, title: 'Optimization', desc: 'Save up to 40%' },
    { icon: Shield, title: 'SOC 2 Ready', desc: 'Enterprise compliance' },
  ];

  const allFeatures = [
    { icon: LayoutDashboard, title: 'Custom Dashboards', color: 'emerald' },
    { icon: TrendingUp, title: 'Cost Forecasting', color: 'blue' },
    { icon: AlertTriangle, title: 'Anomaly Detection', color: 'amber' },
    { icon: DollarSign, title: 'Save 20-40%', color: 'green' },
    { icon: Shield, title: 'SOC 2 Compliant', color: 'purple' },
    { icon: Sparkles, title: 'AI Search', color: 'pink' },
    { icon: BarChart3, title: 'Cost Intelligence Graph', color: 'cyan' },
    { icon: Users, title: 'Team Cost Allocation', color: 'orange' },
    { icon: Cloud, title: 'Multi-Cloud Support', color: 'sky' },
    { icon: PiggyBank, title: 'Savings Tracker', color: 'lime' },
    { icon: Clock, title: 'Budget Guardrails', color: 'rose' },
    { icon: Zap, title: 'Slack/Teams Alerts', color: 'violet' },
  ];

  const handleSkipSplash = () => {
    setSplashFading(true);
    setTimeout(() => setShowSplash(false), 500);
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(16, 185, 129, 0.08) 0%, transparent 50%), radial-gradient(ellipse 60% 50% at 90% 100%, rgba(20, 184, 166, 0.06) 0%, transparent 50%), #ffffff'
    }}>
      {/* Back to home — top-left rescue button so accidental sign-in clickers can escape */}
      <Link
        to="/"
        className="absolute top-4 left-4 sm:top-6 sm:left-6 z-30 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 hover:bg-white border border-slate-200 hover:border-emerald-400 text-slate-700 hover:text-emerald-700 text-xs font-semibold shadow-sm hover:shadow transition-all backdrop-blur"
        data-testid="login-back-to-home"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to home
      </Link>

      {/* Subtle ambient glow circles */}
      <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-emerald-300/15 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-teal-300/15 to-transparent blur-3xl pointer-events-none" />
      
      {/* OTP Verification Modal — dark themed */}
      {otpRequired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md">
          <div className="dark relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-emerald-500/30 via-transparent to-teal-500/20 rounded-3xl blur-2xl pointer-events-none" />
            <div className="relative rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl p-8 max-w-md w-full mx-4 text-white" data-testid="otp-verification-modal">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-500/15 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Verify Your Identity</h2>
                <p className="text-sm text-slate-400 mt-2">
                  We sent a 6-digit code to <span className="font-medium text-slate-200">{otpEmailHint}</span>
                </p>
              </div>
              <form onSubmit={handleOtpVerify} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-[0.5em] font-mono h-14 auth-input"
                    maxLength={6}
                    autoFocus
                    data-testid="otp-code-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={otpCode.length !== 6 || loading}
                  data-testid="otp-verify-btn"
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </Button>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="text-sm text-emerald-400 hover:text-emerald-300"
                    data-testid="otp-resend-btn"
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOtpRequired(false); setOtpCode(''); }}
                    className="text-sm text-slate-400 hover:text-slate-300"
                  >
                    Back to login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Splash Screen - Minimal & Elegant */}
      {showSplash && (
        <div 
          className={`absolute inset-0 z-50 bg-white dark:bg-slate-950 flex items-center justify-center transition-opacity duration-500 ${splashFading ? 'opacity-0' : 'opacity-100'}`}
        >
          {/* Animated gradient background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-emerald-100/40 to-transparent dark:from-emerald-900/20 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}} />
            <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-emerald-100/30 to-transparent dark:from-emerald-900/10 rounded-full blur-3xl animate-pulse" style={{animationDuration: '5s', animationDelay: '1s'}} />
          </div>

          <div className="relative text-center px-6">
            {/* Single elegant dark card with glow ring — matches Sign In / Register style */}
            <div className="dark relative inline-block mb-6">
              <div className="absolute -inset-2 bg-gradient-to-br from-emerald-500/30 to-teal-500/20 rounded-3xl blur-2xl animate-pulse pointer-events-none" />
              <div className="relative rounded-3xl bg-slate-950 border border-slate-800 shadow-2xl shadow-emerald-500/30 overflow-hidden">
                {/* Top accent strip */}
                <div className="h-1 bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500" />
                <div className="px-12 py-10 flex flex-col items-center gap-4">
                  {/* Emblem — transparent, no box (same as navbar) */}
                  <img 
                    src="/icons/cartie-emblem.png" 
                    alt="CARTIEAI" 
                    className="w-32 h-32 object-contain"
                  />
                  {/* Wordmark below — CARTIE white + AI emerald + ™ */}
                  <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white font-['Manrope']" data-testid="splash-wordmark">
                    CARTIE<span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.45em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
                  </h1>
                </div>
              </div>
            </div>
            
            {/* Single Powerful Tagline */}
            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 mb-6 font-light">
              Pay 60% less for AI tokens. Plus the cloud savings.
            </p>
            
            {/* Beta Program Badge */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                <span className="text-emerald-700 dark:text-emerald-300 font-medium text-sm">
                  Save up to <span className="font-bold">70%</span> on AI tokens
                </span>
              </div>
            </div>

            {/* Loading indicator */}
            <div className="flex justify-center">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{animationDelay: '0ms'}} />
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{animationDelay: '150ms'}} />
                <div className="w-2 h-2 rounded-full bg-teal-500 animate-bounce" style={{animationDelay: '300ms'}} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Form — WHITE page, DARK floating card */}
      <div 
        className={`relative min-h-screen flex items-center justify-center p-6 transition-opacity duration-500 ${showSplash ? 'opacity-0' : 'opacity-100'}`}
      >
        <div className="dark w-full max-w-md relative">
          {/* Glow ring behind the dark card */}
          <div className="absolute -inset-1 bg-gradient-to-br from-emerald-500/25 to-teal-500/15 rounded-3xl blur-2xl pointer-events-none" />
          
          {/* The dark card itself */}
          <div className="relative rounded-3xl bg-slate-950 border border-slate-800 shadow-2xl shadow-emerald-500/10 overflow-hidden">
            {/* Top accent strip */}
            <div className="h-1 bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500" />
            
            <div className="p-8 sm:p-10">
              {/* Logo - Centered: emblem on top, wordmark™ below */}
              <div className="flex flex-col items-center justify-center mb-7">
                <img 
                  src="/icons/cartie-emblem.png" 
                  alt="CARTIEAI" 
                  className="w-24 h-24 object-contain mb-2"
                />
                <span className="text-2xl font-extrabold text-white tracking-tight font-['Manrope']" data-testid="login-wordmark">
                  CARTIE<span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.5em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
                </span>
              </div>

              {/* Welcome Text */}
              <div className="text-center mb-7">
                <h2 className="text-xl font-bold text-white">Welcome back</h2>
                <p className="text-slate-400 mt-1 text-sm">Sign in to your account to continue</p>
              </div>

              {/* Auth Card content begins here — dark theme classes will all auto-activate */}
              <div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-900/80 ring-1 ring-slate-700/50 p-1 rounded-xl">
                <TabsTrigger 
                  value="login" 
                  data-testid="login-tab"
                  className="rounded-lg text-slate-300 data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950 data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=active]:shadow-emerald-500/20 transition-all"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  data-testid="register-tab"
                  className="rounded-lg text-slate-300 data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950 data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=active]:shadow-emerald-500/20 transition-all"
                >
                  Register
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@company.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                      className="h-11 auth-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-medium"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                        className="h-11 pr-10 auth-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold border-0 shadow-lg shadow-emerald-500/30" 
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Sign In
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </form>
                
                {/* Enterprise SSO link */}
                <div className="mt-5 relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200 dark:border-slate-700" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white dark:bg-slate-900 px-3 text-slate-400">or</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/sso/login')}
                  className="mt-5 w-full h-11 flex items-center justify-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors"
                  data-testid="sso-login-link"
                >
                  <Shield className="w-4 h-4 text-indigo-500" />
                  Sign in with Enterprise SSO
                </button>
                
                {/* Sign up prompt */}
                <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-sm text-slate-400 dark:text-slate-400 text-center">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setActiveTab('register')}
                      className="text-emerald-600 hover:text-emerald-700 font-medium"
                      data-testid="signup-link"
                    >
                      Sign up for free
                    </button>
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="John Doe"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                      data-testid="register-name-input"
                      className="h-11 auth-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@company.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                      className="h-11 auth-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="Min 8 chars, uppercase, lowercase, number"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                      className="h-11 auth-input"
                    />
                    {registerData.password && (
                      <div className="space-y-1">
                        <div className="flex gap-1">
                          {[
                            registerData.password.length >= 8,
                            /[A-Z]/.test(registerData.password),
                            /[a-z]/.test(registerData.password),
                            /[0-9]/.test(registerData.password)
                          ].map((met, i) => (
                            <div key={i} className={`h-1 flex-1 rounded-full ${met ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-slate-400">
                          {[
                            registerData.password.length >= 8 ? null : '8+ chars',
                            /[A-Z]/.test(registerData.password) ? null : 'uppercase',
                            /[a-z]/.test(registerData.password) ? null : 'lowercase',
                            /[0-9]/.test(registerData.password) ? null : 'number'
                          ].filter(Boolean).join(', ') || 'Strong password'}
                        </p>
                      </div>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold border-0 shadow-lg shadow-emerald-500/30" 
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
              </div>
            </div>
          </div>

          {/* Footer outside the dark card, on white bg */}
          <p className="text-center text-sm text-slate-500 mt-6 relative">
            Secure cloud cost management for modern teams
          </p>
        </div>
      </div>

      {/* Forgot Password Modal — dark themed to match auth aesthetic */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPassword}>
        <DialogContent className="sm:max-w-md dark bg-slate-950 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Mail className="w-5 h-5 text-emerald-400" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {resetSent 
                ? "Check your email for reset instructions"
                : "Enter your email and we'll send you a reset link"
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetSent ? (
            <div className="py-6 text-center">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
              </div>
              <h3 className="font-semibold text-white mb-2">Email Sent!</h3>
              <p className="text-sm text-slate-400 mb-6">
                If an account exists for <span className="font-medium text-slate-200">{forgotEmail}</span>, 
                you'll receive a password reset link shortly.
              </p>
              <Button onClick={closeForgotPassword} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@company.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                  className="h-11 auth-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
                  onClick={closeForgotPassword}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
